import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { DropdownMenu as M } from "radix-ui";
import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

export const DropdownMenu = M.Root;
export const DropdownMenuTrigger = M.Trigger;
export const DropdownMenuGroup = M.Group;
export const DropdownMenuRadioGroup = M.RadioGroup;

export const menuContentClass =
  "z-50 min-w-48 overflow-hidden rounded-2xl border border-line bg-white p-1.5 shadow-[0_18px_50px_rgb(15_23_42/0.14)] data-[state=open]:animate-pop";

export const DropdownMenuContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof M.Content>>(function DropdownMenuContent(
  { className, sideOffset = 6, align = "end", ...props },
  ref,
) {
  return (
    <M.Portal>
      <M.Content ref={ref} sideOffset={sideOffset} align={align} className={cn(menuContentClass, className)} {...props} />
    </M.Portal>
  );
});

const itemClass =
  "relative flex min-h-10 cursor-pointer select-none items-center gap-2.5 rounded-xl px-3 text-sm font-medium text-slate-700 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-slate-500";

export const DropdownMenuItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof M.Item> & { destructive?: boolean }>(function DropdownMenuItem(
  { className, destructive, ...props },
  ref,
) {
  return <M.Item ref={ref} className={cn(itemClass, destructive && "text-danger data-[highlighted]:bg-danger-soft data-[highlighted]:text-danger [&_svg]:text-danger", className)} {...props} />;
});

export const DropdownMenuRadioItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof M.RadioItem>>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <M.RadioItem ref={ref} className={cn(itemClass, "pr-9", className)} {...props}>
      {children}
      <M.ItemIndicator className="absolute right-3">
        <Check className="text-primary!" aria-hidden="true" />
      </M.ItemIndicator>
    </M.RadioItem>
  );
});

export function DropdownMenuLabel({ className, ...props }: ComponentPropsWithoutRef<typeof M.Label>) {
  return <M.Label className={cn("px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400", className)} {...props} />;
}
export function DropdownMenuSeparator({ className, ...props }: ComponentPropsWithoutRef<typeof M.Separator>) {
  return <M.Separator className={cn("-mx-1.5 my-1.5 h-px bg-slate-900/8", className)} {...props} />;
}
